import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import NoticeToast from '../common/NoticeToast';
import { apiUrl } from '../../config/api';
import { apiFetch } from '../../api/client';
import { useNotice } from '../../hooks/useNotice';
import { openOrDownloadDocument, safeFilename } from '../../utils/documentHandling';
import '../chef/ValidationInterface.css';
import './ValidationPedagogique.css';

export default function ValidationPedagogique() {
  const { planId } = useParams();
  const navigate = useNavigate();
  const { notice, showNotice, clearNotice } = useNotice();
  const [searchParams] = useSearchParams();
  const action = searchParams.get('action'); // 'reject' si vient du bouton Refuser
  
  const [gridData, setGridData] = useState(null);
  const [planData, setPlanData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [updating, setUpdating] = useState(false);

  // Panneau de commentaires
  const [selectedElement, setSelectedElement] = useState(null);
  const [commentairePanelOpen, setCommentairePanelOpen] = useState(false);

  // État pour la validation pédagogique
  const [pedagogiqueData, setPedagogiqueData] = useState({
    conformite_conseiller: '', // 'oui' ou 'non'
    commentaire: '',
    recommandation: '' // 'approuvé' ou 'rejeté'
  });

  useEffect(() => {
    const loadGridData = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await apiFetch(`/api/plans/${planId}/grille-data`, {
          auth: true,
        });
        if (!response.ok) {
          if (response.status === 404) {
            setError('La grille de validation n\'a pas encore été générée pour ce plan.');
          } else {
            setError('Erreur lors du chargement de la grille.');
          }
          return;
        }
        const data = await response.json();
        setGridData(data.payload);
        setPlanData(data.plan);

        if (data.payload?.conformite?.conseiller_pedagogique) {
          setPedagogiqueData({
            conformite_conseiller: data.payload.conformite.conseiller_pedagogique,
            commentaire: data.payload.conformite.conseiller_commentaire || '',
            recommandation: data.payload.conformite.conseiller_recommandation || ''
          });
        }
      } catch (err) {
        setError('Impossible de charger la grille de validation.');
      } finally {
        setLoading(false);
      }
    };

    loadGridData();
  }, [planId]);

  // Si action=reject, ouvrir avec conformité à "non"
  useEffect(() => {
    if (action === 'reject' && gridData) {
      setPedagogiqueData(prev => ({ ...prev, conformite_conseiller: 'non' }));
    }
  }, [action, gridData]);

  const handleConformiteChange = (value) => {
    console.log('Conformité changée:', value);
    setPedagogiqueData(prev => ({ ...prev, conformite_conseiller: value }));
  };

  const handleCommentaireChange = (e) => {
    setPedagogiqueData(prev => ({ ...prev, commentaire: e.target.value }));
  };

  const handleSaveValidation = async (recommandation) => {
    // Pas besoin de vérifier conformite_conseiller - permettre la modification à tout moment

    setUpdating(true);
    try {
      // Mettre à jour la grille avec la validation pédagogique
      const updatedPayload = {
        ...gridData,
        conformite: {
          ...gridData.conformite,
          conseiller_pedagogique: pedagogiqueData.conformite_conseiller,
          conseiller_commentaire: pedagogiqueData.commentaire,
          conseiller_recommandation: recommandation
        }
      };

      // Déterminer le nouveau statut du plan
      let newStatus = planData.statut;
      
      // Logique de validation: les 3 doivent valider (chef, conseiller, directeur)
      const chefValide = gridData.conformite?.chef_equipe === 'oui';
      const conseillerValide = pedagogiqueData.conformite_conseiller === 'oui';
      // Si le conseiller refuse (non), le statut devient "non validé"
      if (pedagogiqueData.conformite_conseiller === 'non') {
        newStatus = 'non validé';
      }
      // Si le conseiller valide et que le chef a aussi validé, on passe à "validé"
      // (le directeur validera après)
      else if (conseillerValide && chefValide) {
        newStatus = 'validé';
      }
      // Si le conseiller n'a pas encore choisi, on reste en "analyse"
      else if (!pedagogiqueData.conformite_conseiller) {
        newStatus = 'analyse';
      }

      const response = await apiFetch(`/api/plans/${planId}/grille-data`, {
        method: 'PUT',
        auth: true,
        json: true,
        body: JSON.stringify({ 
          payload: updatedPayload,
          statut: newStatus
        })
      });

      if (!response.ok) throw new Error('Erreur lors de la sauvegarde');

      showNotice(
        `Validation pedagogique ${recommandation === 'approuvé' ? 'approuvee' : recommandation === 'rejeté' ? 'rejetee' : 'sauvegardee'} avec succes!`,
        'success'
      );
      navigate('/conseiller');
    } catch (err) {
      showNotice('Erreur lors de la sauvegarde de la validation pedagogique.', 'error');
      console.error(err);
    } finally {
      setUpdating(false);
    }
  };

  const openCommentairePanel = (elementName, elementLabel) => {
    setSelectedElement({ name: elementName, label: elementLabel });
    setCommentairePanelOpen(true);
  };

  const closeCommentairePanel = () => {
    setCommentairePanelOpen(false);
    setSelectedElement(null);
  };

  const saveElementCommentaire = () => {
    // Les commentaires sont déjà dans gridData, juste fermer le panel
    closeCommentairePanel();
    showNotice('Commentaire enregistre.', 'success');
  };

  // Extract data sections
  const infos = gridData?.infos || {};
  const identification = gridData?.identification || {};
  const presentation = gridData?.presentation || {};
  const conformite = gridData?.conformite || {};

  if (loading) return <div className="validation-loading">Chargement de la grille...</div>;
  if (error) return <div className="validation-error">{error}</div>;

  return (
    <section className={`validation-interface validation-pedagogique ${commentairePanelOpen ? 'with-sidebar' : ''}`}>
      <div className="pedagogique-banner">
        ✅ Validation Pédagogique - Conseiller Pédagogique
      </div>

      <div className="validation-main-content">

      <header className="validation-summary">
        <div className="plan-header-info">
          <h3>📋 {planData?.nom_fichier || 'Plan de cours'}</h3>
          <p style={{ margin: '8px 0 0', color: '#64748b', fontSize: '15px' }}>
            <strong>Cours:</strong> {planData?.cours || 'N/A'} • 
            <strong> Enseignant:</strong> {planData?.enseignant_nom || 'N/A'} • 
            <strong> Date de soumission:</strong> {planData?.date_upload?.slice(0, 10) || 'N/A'}
          </p>
        </div>
        <div className="result-actions">
          <button 
            className="btn-primary" 
            onClick={() => {
              const fileUrl = apiUrl(`/static/plans/${planId}_${safeFilename(planData?.nom_fichier || '')}`);
              openOrDownloadDocument({
                url: fileUrl,
                fileName: planData?.nom_fichier,
                onOpenPdf: () => window.open(fileUrl, '_blank', 'noopener,noreferrer'),
                onNotify: showNotice,
              });
            }}
            style={{ marginRight: '12px' }}
          >
            📄 Visualiser
          </button>
          <button className="btn-outline" onClick={() => navigate('/conseiller')}>
            ← Retour au tableau de bord
          </button>
        </div>
      </header>

      {/* Grille complète en lecture seule SAUF le champ conformité conseiller */}
      <form className="grid-form">
        <div className="pedagogique-section">
          <h2>📝 Informations Générales</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
            <label>
              <strong>Numéro de cours</strong>
              <input type="text" defaultValue={infos.numero_cours || ''} disabled style={{ marginTop: '8px', padding: '12px', borderRadius: '8px', border: '2px solid #e2e8f0', background: '#f8fafc' }} />
            </label>
            <label>
              <strong>Vérificateur</strong>
              <input type="text" defaultValue={infos.verificateur || ''} disabled style={{ marginTop: '8px', padding: '12px', borderRadius: '8px', border: '2px solid #e2e8f0', background: '#f8fafc' }} />
            </label>
            <label>
              <strong>Date</strong>
              <input type="date" defaultValue={infos.date || ''} disabled style={{ marginTop: '8px', padding: '12px', borderRadius: '8px', border: '2px solid #e2e8f0', background: '#f8fafc' }} />
            </label>
          </div>
        </div>

        <div className="pedagogique-section">
          <h2>✅ Conformité et Validation</h2>
          <div className="radio-group">
            <p><strong>Conformité du plan de cours (Chef d'équipe)</strong></p>
            <label><input type="radio" checked={conformite.chef_equipe === 'oui'} disabled /> Oui</label>
            <label><input type="radio" checked={conformite.chef_equipe === 'non'} disabled /> Non</label>
          </div>
          
          {/* CHAMP ÉDITABLE POUR LE CONSEILLER */}
          <div className="radio-group conseiller-editable">
            <p><strong>Conformité Chef d'équipe (Conseiller pédagogique)</strong></p>
            <label style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
              <input 
                type="radio" 
                name="conformite_conseiller"
                value="oui"
                disabled={false}
                checked={pedagogiqueData.conformite_conseiller === 'oui'}
                onChange={(e) => handleConformiteChange('oui')}
                style={{ cursor: 'pointer', width: '22px', height: '22px', pointerEvents: 'auto', accentColor: '#ffa726' }}
              /> 
              <span style={{ fontSize: '16px', fontWeight: '500' }}>Oui, le plan est conforme</span>
            </label>
            <label style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '12px' }}>
              <input 
                type="radio" 
                name="conformite_conseiller"
                value="non"
                disabled={false}
                checked={pedagogiqueData.conformite_conseiller === 'non'}
                onChange={(e) => handleConformiteChange('non')}
                style={{ cursor: 'pointer', width: '22px', height: '22px', pointerEvents: 'auto', accentColor: '#ffa726' }}
              /> 
              <span style={{ fontSize: '16px', fontWeight: '500' }}>Non, le plan nécessite des corrections</span>
            </label>
          </div>

          <div className="radio-group">
            <p><strong>Validation par échantillonnage (Directeur adjoint aux études)</strong></p>
            <label><input type="radio" checked={conformite.directeur_adjoint === 'oui'} disabled /> Oui</label>
            <label><input type="radio" checked={conformite.directeur_adjoint === 'non'} disabled /> Non</label>
          </div>

          <div className="commentaire-section conseiller-editable">
            <label>
              <strong>💬 Commentaires et recommandations du conseiller pédagogique</strong>
              <textarea
                value={pedagogiqueData.commentaire}
                onChange={handleCommentaireChange}
                placeholder="Ajoutez vos commentaires et recommandations détaillées ici..."
                rows="6"
              />
            </label>
          </div>
        </div>

        <div className="pedagogique-section">
          <h2>📋 Éléments du Plan de Cours</h2>
          <h3>1. Page d'identification (conformité avec plan-cadre*)</h3>
          <table>
            <thead><tr><th>Élément</th><th>Conforme</th><th>Commentaires</th><th>Action</th></tr></thead>
            <tbody>
              <tr>
                <td><strong>Numéro du cours</strong></td>
                <td><input type="checkbox" checked={identification.numero_cours || false} disabled /></td>
                <td><textarea defaultValue={identification.numero_cours_comment || ''} disabled /></td>
                <td>
                  <button 
                    type="button" 
                    className="btn-comment"
                    onClick={() => openCommentairePanel('numero_cours', 'Numéro du cours')}
                  >
                    💬
                  </button>
                </td>
              </tr>
              <tr>
                <td><strong>Titre du cours</strong></td>
                <td><input type="checkbox" checked={identification.titre_cours || false} disabled /></td>
                <td><textarea defaultValue={identification.titre_cours_comment || ''} disabled /></td>
                <td>
                  <button 
                    type="button" 
                    className="btn-comment"
                    onClick={() => openCommentairePanel('titre_cours', 'Titre du cours')}
                  >
                    💬
                  </button>
                </td>
              </tr>
              <tr>
                <td>Programme</td>
                <td><input type="checkbox" checked={identification.programme || false} disabled /></td>
                <td><textarea defaultValue={identification.programme_comment || ''} disabled /></td>
                <td>
                  <button 
                    type="button" 
                    className="btn-comment"
                    onClick={() => openCommentairePanel('programme', 'Programme')}
                  >
                    💬
                  </button>
                </td>
              </tr>
              <tr>
                <td>Discipline</td>
                <td><input type="checkbox" checked={identification.discipline || false} disabled /></td>
                <td><textarea defaultValue={identification.discipline_comment || ''} disabled /></td>
                <td>
                  <button 
                    type="button" 
                    className="btn-comment"
                    onClick={() => openCommentairePanel('discipline', 'Discipline')}
                  >
                    💬
                  </button>
                </td>
              </tr>
            </tbody>
          </table>

          <h3>2. Présentation du cours</h3>
          <table>
            <thead><tr><th>Élément</th><th>Conforme</th><th>Commentaires</th></tr></thead>
            <tbody>
              <tr><td><strong>Description du cours</strong></td><td><input type="checkbox" checked={presentation.description || false} disabled /></td><td><textarea defaultValue={presentation.description_comment || ''} disabled /></td></tr>
              <tr><td>Place du cours dans le programme</td><td><input type="checkbox" checked={presentation.place_programme || false} disabled /></td><td><textarea defaultValue={presentation.place_programme_comment || ''} disabled /></td></tr>
            </tbody>
          </table>
        </div>

        <div className="action-buttons-fixed">
          <button
            className="btn-save-conformite"
            type="button"
            onClick={() => handleSaveValidation('en_cours')}
            disabled={updating}
          >
            💾 Sauvegarder la conformité
          </button>
          <button
            className="btn-approve"
            type="button"
            onClick={() => handleSaveValidation('approuvé')}
            disabled={updating}
          >
            ✓ Approuver le plan
          </button>
          <button
            className="btn-reject"
            type="button"
            onClick={() => handleSaveValidation('rejeté')}
            disabled={updating}
          >
            ✗ Rejeter le plan
          </button>
        </div>

        <div className="view-full-grid">
          <button 
            type="button"
            className="btn-view-full"
            onClick={() => navigate(`/grid/${planId}`)}
          >
            📋 Voir la grille complète détaillée
          </button>
        </div>
      </form>
      </div>

      {/* Panneau latéral de commentaires */}
      {commentairePanelOpen && selectedElement && (
        <div className="commentaire-sidebar">
          <div className="sidebar-header">
            <h3>💬 Commentaire</h3>
            <button 
              type="button" 
              className="btn-close-sidebar"
              onClick={closeCommentairePanel}
            >
              ✕
            </button>
          </div>
          <div className="sidebar-content">
            <p className="element-name"><strong>Élément:</strong> {selectedElement.label}</p>
            <label>
              <strong>Votre commentaire</strong>
              <textarea
                rows="8"
                placeholder="Ajoutez votre commentaire détaillé ici..."
                defaultValue={identification[`${selectedElement.name}_comment`] || ''}
                style={{ marginTop: '10px', width: '100%', padding: '12px', borderRadius: '8px', border: '2px solid #e2e8f0', fontSize: '14px' }}
              />
            </label>
          </div>
          <div className="sidebar-actions">
            <button 
              type="button"
              className="btn-save-comment"
              onClick={saveElementCommentaire}
            >
              ✓ Enregistrer
            </button>
            <button 
              type="button"
              className="btn-cancel"
              onClick={closeCommentairePanel}
            >
              Annuler
            </button>
          </div>
        </div>
      )}

      <NoticeToast notice={notice} onClose={clearNotice} />
    </section>
  );
}
